import React from 'react';
import { ShieldCheck, Certificate, IdentificationCard, Check } from '@phosphor-icons/react';

/**
 * Three-tile "Trust Badges" group displayed near the contractor / worker name.
 * Tactical Brutalism: 2px border, hard offset shadow, no gradients.
 *
 * Props:
 *  - licensed   : boolean (license + insurance on file → Verified Pro)
 *  - insured    : boolean (alias of `licensed` for now — same verification flow)
 *  - idVerified : boolean (KYC / ID upload completed)
 *  - size       : "sm" | "md"   default "md"
 *  - testIdPrefix : string for data-testids
 */
const VerifiedBadges = ({
  licensed = false,
  insured = false,
  idVerified = false,
  size = 'md',
  testIdPrefix = 'verified',
}) => {
  const sm = size === 'sm';
  const padX = sm ? 'px-1.5' : 'px-2';
  const padY = sm ? 'py-0.5' : 'py-1';
  const text = sm ? 'text-[9px]' : 'text-[10px]';
  const iconSize = sm ? 10 : 12;

  const badge = (active, Icon, label, key) => (
    <span
      key={key}
      className={`inline-flex items-center gap-1 ${padX} ${padY} ${text} font-black uppercase tracking-widest border-2 border-black rounded ${
        active
          ? 'bg-emerald-500 text-white shadow-[2px_2px_0px_#000]'
          : 'bg-neutral-200 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-500 line-through'
      }`}
      data-testid={`${testIdPrefix}-${key}-${active ? 'on' : 'off'}`}
      title={active ? `${label} verified` : `${label} not on file yet`}
    >
      <Icon size={iconSize} weight="fill" />
      {label}
      {active && <Check size={iconSize} weight="bold" className="ml-0.5" />}
    </span>
  );

  return (
    <div className="inline-flex flex-wrap items-center gap-1.5" data-testid={`${testIdPrefix}-group`}>
      {badge(licensed, Certificate, 'Licensed', 'licensed')}
      {badge(insured, ShieldCheck, 'Insured', 'insured')}
      {badge(idVerified, IdentificationCard, 'ID', 'id')}
    </div>
  );
};

export default VerifiedBadges;
